/**
 * Procure to pay, completed on 2026-09-28: the request's approval, the RFQ letter and its dispatch log, a supplier's
 * refusal to quote, purchase orders in their own right (direct, placed, changed, stopped, repeated), the supplier's
 * invoice and the payment with their own facts, the purchase demand still waiting for a request, and the supplier
 * directory. The rules live server-side in `engines/procure.py`; these are the calls and the shapes.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { invalidateProcure, SourcingRequestSchema } from "./api";
import { frappe } from "./frappe";

const str = z.string().nullable().optional().transform((v) => v ?? "");
const num = z.number().nullable().optional().transform((v) => v ?? 0);
const M = "onesphere.api.procure";
const O = "onesphere.api.procure_orders";

// ---------------------------------------------------------------- the request: approval, letter, dispatch, no quote, direct order
function requestMutation<TIn>(method: string, body: (input: TIn) => Record<string, unknown>) {
  return function useRequestMutation() {
    const qc = useQueryClient();
    return useMutation({ mutationFn: async (input: TIn) => SourcingRequestSchema.parse(await frappe.post(method, body(input))), onSuccess: () => invalidateProcure(qc) });
  };
}
export const useDecideRequest = requestMutation(`${M}.decide_request`, (i: { name: string; decision: "Approved" | "Rejected"; note: string }) => i);
export const useRecordNoQuote = requestMutation(`${M}.record_no_quote`, (i: { name: string; supplier: string; reason: string }) => i);
export const useLogDispatch = requestMutation(`${M}.log_dispatch`,
  (i: { name: string; supplier: string; channel: string; sent_to: string; note?: string; send?: boolean }) => ({ ...i, send: i.send ? 1 : 0 }));
export const useDirectOrder = requestMutation(`${O}.direct_order`,
  (i: { name: string; supplier: string; rates: Record<string, number>; reason: string }) => ({ ...i, rates: JSON.stringify(i.rates) }));

export const RfqLetterSchema = z.object({
  request: z.string(), rfq: z.string(), date: str, reply_by: str, subject: str, message: str, terms: str, questions: z.array(z.string()).default([]), delivery_location: str, buyer: str,
  can_email: z.boolean().optional(),
  company: z.object({ company_name: str, email: str, phone_no: str, tax_id: str }),
  supplier: z.object({ name: z.string(), supplier_name: str, country: str, contact: str, email: str, phone: str, address: str }),
  items: z.array(z.object({ item_code: z.string(), item_name: str, qty: num, uom: str, specification: str })).default([]),
});
export type RfqLetter = z.infer<typeof RfqLetterSchema>;
export function useRfqLetter(name: string | undefined, supplier: string | undefined) {
  return useQuery({ queryKey: ["rfq_letter", name ?? "", supplier ?? ""], enabled: Boolean(name && supplier),
    queryFn: async () => RfqLetterSchema.parse(await frappe.call(`${M}.rfq_letter`, { name, supplier })) });
}

// ---------------------------------------------------------------- purchase orders
const MatchSchema = z.object({ ok: z.boolean(), issues: z.array(z.string()).default([]) }).passthrough();
const OriginSchema = z.object({ request: str, event: str, budget_cdf: num, approved: z.boolean() });
export const PurchaseOrderRowSchema = z.object({
  name: z.string(), supplier: str, supplier_name: str, company: str, transaction_date: str, schedule_date: str, status: str, docstatus: z.number(), grand_total: num, currency: str,
  base_grand_total: num, per_received: num, per_billed: num, origin: OriginSchema,
});
export type PurchaseOrderRow = z.infer<typeof PurchaseOrderRowSchema>;
export const PurchaseOrderSchema = z.object({
  order: z.object({ name: z.string(), supplier: str, supplier_name: str, company: str, status: str, docstatus: z.number(), transaction_date: str, schedule_date: str, currency: str,
    conversion_rate: num, grand_total: num, base_grand_total: num, per_received: num, per_billed: num }),
  items: z.array(z.object({ name: z.string(), item_code: z.string(), item_name: str, qty: num, received_qty: num, billed_qty: num, rate: num, amount: num, uom: str, schedule_date: str })).default([]),
  receipts: z.array(z.object({ name: z.string(), posting_date: str, grand_total: num, currency: str, status: str })).default([]),
  invoices: z.array(z.object({ name: z.string(), posting_date: str, bill_no: str, bill_date: str, grand_total: num, currency: str, outstanding_amount: num, status: str, remarks: str, match: MatchSchema })).default([]),
  payments: z.array(z.object({ name: z.string(), posting_date: str, mode_of_payment: str, paid_from: str, reference_no: str, reference_date: str, allocated_amount: num, invoice: str })).default([]),
  open_receipts: z.array(z.string()).default([]), outstanding: num, origin: OriginSchema, approved_total_cdf: num,
  actions: z.object({ place: z.boolean(), change: z.boolean(), stop: z.string().nullable().optional(), repeat: z.boolean(), invoice: z.boolean(), pay: z.boolean() }),
  accounts: z.array(z.object({ name: z.string(), account_type: str, account_currency: str })).default([]), modes: z.array(z.string()).default([]),
});
export type PurchaseOrder = z.infer<typeof PurchaseOrderSchema>;

export function usePurchaseOrders(search: string, state: string) {
  return useQuery({ queryKey: ["purchase_orders", search, state],
    queryFn: async () => z.object({ orders: z.array(PurchaseOrderRowSchema) }).parse(await frappe.call(`${O}.purchase_orders`, { ...(search ? { search } : {}), ...(state ? { state } : {}) })).orders });
}
export function usePurchaseOrder(name: string | undefined) {
  return useQuery({ queryKey: ["purchase_order", name ?? ""], enabled: Boolean(name), queryFn: async () => PurchaseOrderSchema.parse(await frappe.call(`${O}.purchase_order`, { name })) });
}
function orderMutation<TIn>(method: string, body: (input: TIn) => Record<string, unknown>) {
  return function useOrderMutation() {
    const qc = useQueryClient();
    return useMutation({ mutationFn: async (input: TIn) => PurchaseOrderSchema.parse(await frappe.post(`${O}.${method}`, body(input))), onSuccess: () => invalidateProcure(qc) });
  };
}
export const usePlaceOrder = orderMutation("place_order", (i: { name: string }) => i);
export const useStopOrder = orderMutation("stop_order", (i: { name: string; reason: string }) => i);
export const useChangeOrder = orderMutation("change_order",
  (i: { name: string; changes: Record<string, { qty?: number; rate?: number; schedule_date?: string }>; reason: string }) => ({ ...i, changes: JSON.stringify(i.changes) }));
export const usePostInvoice = orderMutation("post_invoice",
  (i: { name: string; bill_no: string; bill_date: string; amount: number; override?: boolean; reason?: string }) => ({ ...i, override: i.override ? 1 : 0 }));
export const useMakePayment = orderMutation("make_payment",
  (i: { name: string; amount: number; account: string; mode_of_payment: string; reference_no: string; reference_date: string; posting_date: string }) => i);
export function useRepeatOrder() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (i: { name: string }) => SourcingRequestSchema.parse(await frappe.post(`${O}.repeat_order`, i)), onSuccess: () => invalidateProcure(qc) });
}

// ---------------------------------------------------------------- upstream demand and the supplier directory
export const MaterialRequestSchema = z.object({
  name: z.string(), title: str, company: str, transaction_date: str, schedule_date: str, docstatus: z.number(), status: str, owner: str,
  items: z.array(z.object({ item_code: z.string(), item_name: str, qty: num, uom: str, warehouse: str })).default([]),
});
export type MaterialRequest = z.infer<typeof MaterialRequestSchema>;
export function useMaterialRequests(enabled = true) {
  return useQuery({ queryKey: ["material_requests"], enabled,
    queryFn: async () => z.object({ material_requests: z.array(MaterialRequestSchema) }).parse(await frappe.call(`${O}.material_requests`, {})).material_requests });
}
export const DirectorySupplierSchema = z.object({
  name: z.string(), supplier_name: str, supplier_group: str, country: str, default_currency: str, on_hold: num, disabled: num, tax_id: str,
  qualifications: z.array(z.object({ item_group: z.string(), status: z.string(), valid_until: str })).default([]),
  open_file: z.object({ name: z.string(), status: z.string() }).nullable().optional(),
});
export type DirectorySupplier = z.infer<typeof DirectorySupplierSchema>;
export function useSupplierDirectory(search: string, enabled = true) {
  return useQuery({ queryKey: ["supplier_directory", search], enabled,
    queryFn: async () => z.object({ suppliers: z.array(DirectorySupplierSchema) }).parse(await frappe.call(`${O}.suppliers`, search ? { search } : {})).suppliers });
}

/** The same reasons the server gives, in words — so a refusal or a disabled button can say why. */
export function problemKey(problem: string): { key: string; value?: string } {
  const [key, value] = problem.split(":");
  return { key, value };
}
